import type { Dictionary } from './types';

/**
 * English.
 *
 * Written as English rather than as a translation of the Portuguese. The two are not
 * the same sentence twice: "Uma conta, uma licença, três lugares" is a rhythm in
 * Portuguese, and carrying the rhythm into English would produce a headline nobody
 * would say out loud.
 */
export const en: Dictionary = {
  meta: {
    title: 'Orobas: OrobasHipercube and Orobas Work for macOS',
    description:
      'OrobasHipercube and Orobas Work: two native macOS apps written in SwiftUI. A super powerful AI agent that works on your actual project, and a native IDE. In beta. One account, one lifetime licence. A product of M2ON.',
    ogTitle: 'Orobas: a super powerful AI agent and a native IDE.',
    ogDescription:
      'OrobasHipercube is a super powerful AI agent that acts on your real project. Orobas Work is the native IDE where the work happens. Native SwiftUI, macOS 26, in beta.',
  },

  chrome: {
    skipToContent: 'Skip to content',
    brandName: 'Orobas',
    navMain: 'Main',
    navApps: 'Apps',
    navPlans: 'Plans',
    navSkins: 'Skins',
    signIn: 'Sign in',
    myAccount: 'My account',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    languageLabel: 'Language',
    platformBadge: 'for macOS',
    downloadInstaller: 'Download installer',
  },

  footer: {
    blurb:
      'Two native macOS apps, written in SwiftUI. No Electron, no WebKit, no telemetry. A product of M2ON.',
    paymentsLine: 'Payments through Mercado Pago.',
    rights: '© {year} {company} - {legalId}.',
    reserved: 'ALL RIGHTS RESERVED.',
    companyName: 'M2ON LTDA',
    legalId: 'CNPJ: 68.657.000/0001-00',
    companyLinkLabel: 'M2ON, the company website (opens in a new tab)',
  },

  home: {
    heroShortcut: 'opens {app} from anywhere',
    heroTitleLine1: 'Two native apps.',
    heroTitleLine2: 'One agent.',
    heroBody:
      '*{agent}* is a super powerful AI agent that acts on your real project. *{work}* is the native IDE where the work happens. In beta, focused on software developers, with the goal of being usable for any kind of work on macOS. Written in SwiftUI, a product of M2ON.',
    ctaSignIn: 'Sign in',
    ctaAccount: 'My account',
    ctaMeetApps: 'See the apps',
    plateAlt: 'Compact window of {app}',
    wallpaperAlt: 'Orobas wallpaper: the crest in a white line drawing on a dark background.',
    opensApp: 'opens {app}',
    ownedBadge: 'Licensed on this account',
    notOwnedBadge: 'Not licensed on this account yet',
    togetherTitle: 'One account, one licence, three places',
    togetherBody:
      'You sign in with the same email on the site and in both apps. A licence is a row in the database, not a file on your computer, so it is valid in all three at once. Buying on the site unlocks both apps.',
    togetherColumns: [
      {
        title: 'On the site',
        body: 'The account shows what is licensed and where to download each installer. This is where the purchase happens.',
      },
      {
        title: 'Between the two apps',
        body: 'The session lives in a single file, and a change to it is announced between the processes. Signing in to one signs in to the other, sign out included.',
      },
      {
        title: 'The gate',
        body: 'Each app only opens when there is a valid session and a licence for that app. The decision is made in the database, the same way in both.',
      },
    ],
    requirementsLine: 'Requirements: {requirements}',
    installerTitle: 'Two apps, one installer',
    installerBody:
      'The Orobas Installer downloads the newest build of each app, verifies it and puts it in /Applications. One download instead of two. Right-click the app and choose Open the first time.',
    installerDownload: 'Download the installer',
    installerVersion: 'Version {version}',
  },

  pricing: {
    title: 'A lifetime licence per app',
    body:
      'You pay once and use it for good, with no subscription and no usage cap. The apps are in beta. Running Ollama on your own machine makes the token cost zero, so the licence is the only thing you paid.',
    loading: 'Loading plans',
    retry: 'Try again',
    emptyBody:
      'No plan is on sale right now. Email {email} and we will let you know when one opens.',
    footerNote:
      'Payment through Mercado Pago. The licence is activated as soon as the payment clears.',
    onceLabel: 'one-off payment, for life',
    bundleBadge: 'Bundle',
    unavailable: 'Unavailable',
    alreadyOwned: 'Already yours',
    signInToBuy: 'Sign in to buy',
    buy: 'Buy',
    openingPayment: 'Opening payment…',
    catalogueFailed: 'The plans could not be loaded.',
    checkoutFailed: 'The payment could not be started.',
    teaserTitle: 'One account. Both apps.',
    teaserBody:
      'Buy once and use the same account on the site and in both macOS apps. The licence is the same in all three.',
    teaserCta: 'See plans',
  },

  account: {
    crestAlt: 'Orobas crest',
    loadingAccount: 'Loading your account',
    signInTitle: 'Sign in',
    signUpTitle: 'Create your account',
    signInBody:
      'The same account works on the site, in OrobasHipercube and in Orobas Work.',
    signUpBody:
      'One account for both apps and for the site. The licence is read from either side.',
    emailLabel: 'Email',
    passwordLabel: 'Password',
    signingIn: 'Signing in…',
    signingUp: 'Creating account…',
    signInAction: 'Sign in',
    signUpAction: 'Create account',
    signInWithGoogle: 'Sign in with Google',
    signInWithGitHub: 'Sign in with GitHub',
    orDivider: 'or',
    noAccountYet: 'No account yet?',
    hasAccount: 'Already have an account?',
    createOne: 'Create one',
    confirmTitle: 'Confirm your email',
    confirmBody:
      'The account was created for *{email}*. Open the link that arrived in your inbox to confirm it. Without that, signing in will not work.',
    checking: 'Checking…',
    confirmedSignIn: 'I have confirmed it, sign me in',
    wrongPassword: 'That password does not match.',
    backToSignIn: 'Back to sign in',
    accountLabel: 'Account',
    yourAccount: 'Your account',
    signOut: 'Sign out',
    licensesTitle: 'Your licences',
    refreshing: 'Refreshing…',
    refresh: 'Refresh',
    noLicensesBody:
      'No app is licensed on this account yet. As soon as a payment clears, the licence appears here and in both apps at the same time.',
    viewPlans: 'See plans',
    lifetimeLicense: 'Lifetime licence',
    licensedFallback: 'Licensed',
    validUntil: 'until {date}',
    installersTitle: 'Installers',
    download: 'Download',
    versionLabel: 'Version {version}',
    gatekeeperNote:
      'Both packages are signed ad-hoc and have not been through Apple’s notarization. The first time, right-click the app inside the zip and choose Open. Gatekeeper only warns once.',
    oauthFailedTitle: 'Could not complete sign-in',
    oauthFailedBody: 'Try again, or sign in with your email and password.',
    order: {
      readFailedTitle: 'The order could not be read',
      signInRequiredTitle: 'Sign in to see this order',
      signInRequiredBody: 'This page shows the result of an order made by the signed-in account.',
      checkingTitle: 'Checking your payment…',
      checkingBody: 'This usually takes a few seconds.',
      approvedTitle: 'Payment confirmed',
      approvedBody:
        'Your licence is live. It already covers OrobasHipercube, Orobas Work and this account.',
      incompleteTitle: 'The payment did not complete',
      incompleteBody: 'Nothing was charged and no licence was activated.',
      expiredTitle: 'This order expired',
      expiredBody: 'The payment method expired before it was confirmed.',
      pendingTitle: 'Waiting for confirmation',
      pendingBody: 'Mercado Pago is still confirming the payment. This page updates on its own.',
      goToAccount: 'Go to my account',
    },
  },

  skinNFT: {
    title: 'OrobasHipercube Skins',
    subtitle: 'Customize your agent with exclusive accessories',
    owned: 'Skins you own',
    available: 'Available skins',
    equip: 'Equip',
    unequip: 'Unequip',
    purchase: 'Purchase',
    loading: 'Loading skins…',
    emptyOwned: 'You don\'t own any skins yet. Buy from the store to customize your OrobasHipercube!',
    emptyAvailable: 'No skins available at the moment.',
    slotHat: 'Hat',
    slotGlasses: 'Glasses',
    slotEye: 'Eyes',
    slotNose: 'Nose',
    slotMouth: 'Mouth',
    slotBody: 'Body',
    rarityCommon: 'Common',
    rarityRare: 'Rare',
    rarityEpic: 'Epic',
    rarityLegendary: 'Legendary',
    equippedBadge: 'Equipped',
    syncMessage: 'Your skins sync automatically with the OrobasHipercube app on macOS.',
    previewTitle: 'Preview',
    animationNote: 'Eye, nose, and mouth accessories follow your agent\'s animation.',
    editionLabel: 'Edition {number}',
    editionShort: '#{number}',
  },

  callback: {
    crestAlt: 'Orobas crest',
    openingTitle: 'Opening {app}…',
    openingBody: 'One moment.',
    blockedTitle: '{app} did not open automatically',
    blockedBody:
      'Some browsers block the hand-off to an app. Press the button below to open {app} and finish signing in.',
    openAppCta: 'Open {app}',
    declinedTitle: 'Sign-in cancelled',
    declinedCancelled: 'You cancelled the Google sign-in.',
    declinedGeneric: 'Google refused the sign-in.',
    unknownTitle: 'This link is not from an app',
    unknownBody:
      'The address that opened this page does not name any Orobas app, so there is nowhere to send the sign-in back to.',
    incompleteTitle: 'Sign-in incomplete',
    incompleteBody:
      '{missing} did not arrive this time. Open the app and start the sign-in again. It takes a few seconds and brings you back here.',
    signInHere: 'Sign in on the site',
  },

  api: {
    offline: 'No connection to the server. Check the internet and try again.',
    unexpected: 'Something went wrong. Try again.',
    signInFailed: 'Could not sign in. Try again.',
    sessionExpired: 'Session expired. Sign in again.',
    alreadyRegistered: 'This email already has an account. Sign in with your password.',
    accountOffline: 'No connection to the accounts server.',
    badCredentials: 'Wrong email or password.',
    emailNotConfirmed: 'Confirm your email before signing in. The link is in your inbox.',
    passwordTooShort: 'The password needs at least 6 characters.',
    invalidEmail: 'That email does not look valid.',
    rateLimited: 'Too many attempts. Wait a moment and try again.',
  },
};